import React from "react";
import i18n from "i18next";
import {useTranslation} from "react-i18next";
import {useAppContext} from "./AppContext";
import {useAuth} from "./AuthContext";
import {useToast} from "./ToastContext";
import {APP_NAME} from "../App";
import ApiCmsCtrl from "../api/ApiCmsCtrl";

/**
 * Édition en place des libellés du tableau de bord (réanimation / SPARES).
 *
 * Les corrections saisies par un utilisateur ADMIN ou WRITER sont stockées dans
 * le document CMS existant (aucun changement backend / redéploiement) sous la
 * sous-clé générique `config.config.labelOverrides`, et appliquées au runtime.
 *
 * Deux familles de libellés :
 *  - `keys`   : libellés adossés à une clé i18n (titres de sections, libellés de
 *               champs `spares.char.*`, en-têtes de colonnes `mapping.rea.*`,
 *               bascules géo `spares.geo.*`, etc.). Résolus par les composants
 *               (surcharge d'abord, sinon `t(clé, repli)`), et — pour rester
 *               cohérent avec les `t()` non enveloppés — injectés dans i18n.
 *  - `values` : libellés de valeurs de données brutes (CHU, PRI, CHMILetPIC…),
 *               qui n'ont aucune clé i18n. Résolus par simple lookup
 *               `values[lng][champ][valeurBrute]`.
 *
 *  - `hidden` : éléments masqués pour les lecteurs (champs entiers, ou valeurs
 *               individuelles d'un menu). Non linguistique : stocké à plat.
 *               En mode « Éditer », les éléments masqués restent visibles (grisés)
 *               afin de pouvoir les ré-afficher.
 *
 * Format stocké :
 *  {
 *    keys:   { fr: { "<i18nKey>": "texte" }, en: {...} },
 *    values: { fr: { "<champ>": { "<valeurBrute>": "texte" } }, en: {...} },
 *    hidden: { fields: ["<champ>"], values: { "<champ>": ["<valeurBrute>"] } }
 *  }
 */

const NS = "translation";
export const LABEL_LNGS = ["fr", "en"];
const LABEL_LNG_META = {fr: "Français", en: "English"};

const LabelOverridesContext = React.createContext(null);

function emptyStore() {
    return {keys: {fr: {}, en: {}}, values: {fr: {}, en: {}}, hidden: {fields: [], values: {}}};
}

/** Lit (et clone en profondeur) la surcharge depuis le document CMS. */
function readStore(config) {
    const s = emptyStore();
    const raw = config?.config?.labelOverrides;
    if (!raw || typeof raw !== "object") return s;
    LABEL_LNGS.forEach((lng) => {
        if (raw.keys && typeof raw.keys[lng] === "object" && raw.keys[lng]) {
            s.keys[lng] = {...raw.keys[lng]};
        }
        if (raw.values && typeof raw.values[lng] === "object" && raw.values[lng]) {
            s.values[lng] = JSON.parse(JSON.stringify(raw.values[lng]));
        }
    });
    if (raw.hidden && typeof raw.hidden === "object") {
        if (Array.isArray(raw.hidden.fields)) s.hidden.fields = [...raw.hidden.fields];
        if (raw.hidden.values && typeof raw.hidden.values === "object") {
            Object.entries(raw.hidden.values).forEach(([field, list]) => {
                if (Array.isArray(list)) s.hidden.values[field] = [...list];
            });
        }
    }
    return s;
}

/** Langue courante normalisée sur l'ensemble supporté (fr par défaut). */
function normalizeLng(i18nInstance) {
    const raw = (i18nInstance?.resolvedLanguage || i18nInstance?.language || "fr");
    const short = String(raw).split("-")[0];
    return LABEL_LNGS.includes(short) ? short : "fr";
}

export function LabelOverridesProvider({children}) {
    const {t, i18n: i18nInstance} = useTranslation();
    const {config, setConfig, editMode} = useAppContext();
    const {user} = useAuth();
    const {showToast} = useToast();

    const role = user?.instances?.[APP_NAME]?.role;
    const canEdit = role === "ADMIN" || role === "WRITER";
    const lng = normalizeLng(i18nInstance);

    // Valeurs i18n d'origine capturées avant première surcharge (pour restaurer
    // proprement sur "Réinitialiser").
    const originalsRef = React.useRef({fr: {}, en: {}});

    const store = React.useMemo(() => readStore(config), [config]);

    // Injecte une clé surchargée (ou restaure l'original) dans i18n, afin que les
    // appels `t()` non enveloppés dans un <EditableLabel> reflètent aussi la
    // correction (ex. sélecteur de colonnes des filtres dynamiques).
    const applyKeyToI18n = React.useCallback((key, value, forLng) => {
        if (!(key in originalsRef.current[forLng])) {
            originalsRef.current[forLng][key] = i18n.getResource(forLng, NS, key);
        }
        const effective = (value != null && value !== "")
            ? value
            : originalsRef.current[forLng][key];
        i18n.addResource(forLng, NS, key, effective);
    }, []);

    // (Ré)applique toutes les surcharges de clés à i18n au montage, au chargement
    // des ressources et au changement de langue.
    React.useEffect(() => {
        function applyAll() {
            LABEL_LNGS.forEach((l) => {
                Object.entries(store.keys[l] || {}).forEach(([key, value]) => {
                    if (value) applyKeyToI18n(key, value, l);
                });
            });
        }
        applyAll();
        i18n.on("languageChanged", applyAll);
        i18n.on("loaded", applyAll);
        return () => {
            i18n.off("languageChanged", applyAll);
            i18n.off("loaded", applyAll);
        };
    }, [store, applyKeyToI18n]);

    // ── Résolution (lecture) ────────────────────────────────────────────────
    const getKeyLabel = React.useCallback((i18nKey, fallback) => {
        const override = store.keys[lng]?.[i18nKey];
        if (override != null && override !== "") return override;
        return t(i18nKey, fallback);
    }, [store, lng, t]);

    /** Renvoie {fr, en} pour préremplir l'éditeur d'une clé. */
    const getKeyOverride = React.useCallback((i18nKey) => ({
        fr: store.keys.fr?.[i18nKey] ?? "",
        en: store.keys.en?.[i18nKey] ?? "",
    }), [store]);

    const resolveValue = React.useCallback((field, rawValue) => {
        if (rawValue == null) return rawValue;
        const override = store.values[lng]?.[field]?.[rawValue];
        return (override != null && override !== "") ? override : rawValue;
    }, [store, lng]);

    /**
     * Comme `resolveValue`, mais avec un repli explicite au lieu de la valeur
     * brute. Nécessaire pour les menus dont l'affichage par défaut n'est PAS la
     * valeur brute (ex. `consoddj1000jh_c3g` affiché « C3G », résistances mises
     * en forme en HTML). Renvoie `fallback` tant qu'aucune surcharge n'existe.
     */
    const resolveValueOr = React.useCallback((field, rawValue, fallback) => {
        if (rawValue == null) return fallback;
        const override = store.values[lng]?.[field]?.[rawValue];
        return (override != null && override !== "") ? override : fallback;
    }, [store, lng]);

    /** Renvoie {fr, en} pour préremplir l'éditeur d'une valeur. */
    const getValueOverride = React.useCallback((field, rawValue) => ({
        fr: store.values.fr?.[field]?.[rawValue] ?? "",
        en: store.values.en?.[field]?.[rawValue] ?? "",
    }), [store]);

    // ── Visibilité ──────────────────────────────────────────────────────────
    /** Le champ entier est-il masqué pour les lecteurs ? */
    const isFieldHidden = React.useCallback(
        (field) => store.hidden.fields.includes(field),
        [store]
    );

    /** Cette valeur de menu est-elle masquée pour les lecteurs ? */
    const isValueHidden = React.useCallback(
        (field, rawValue) => (store.hidden.values[field] || []).includes(rawValue),
        [store]
    );

    /** Liste des valeurs masquées d'un champ (pour préremplir l'éditeur). */
    const getHiddenValues = React.useCallback(
        (field) => [...(store.hidden.values[field] || [])],
        [store]
    );

    /**
     * Filtre une liste de valeurs pour l'affichage d'un menu. Une valeur masquée
     * disparaît TOUJOURS du menu, y compris en mode « Éditer » : le ré-affichage
     * se fait depuis le dialogue de renommage, qui reçoit lui la liste complète.
     */
    const visibleValues = React.useCallback((field, list) => {
        const hidden = store.hidden.values[field] || [];
        if (hidden.length === 0) return list || [];
        return (list || []).filter((v) => !hidden.includes(v));
    }, [store]);

    // ── Persistance (écriture) ──────────────────────────────────────────────
    const persist = React.useCallback(async (nextStore) => {
        const newConfig = JSON.parse(JSON.stringify(config || {}));
        if (!newConfig.config || typeof newConfig.config !== "object") newConfig.config = {};
        newConfig.config.labelOverrides = nextStore;
        const {error} = await ApiCmsCtrl.updateCmsTemplate(newConfig);
        if (error) {
            showToast({msg: t("labels.saveError", "Erreur lors de l'enregistrement du libellé"), severity: "error"});
            throw error;
        }
        setConfig(newConfig);
        showToast({msg: t("labels.saved", "Libellé enregistré"), severity: "success"});
    }, [config, setConfig, showToast, t]);

    /**
     * Enregistre la surcharge d'une clé i18n (titres, libellés de champ,
     * en-têtes de colonnes…). `byLng` = { fr, en } ; une valeur vide = suppression
     * de la surcharge pour cette langue (restauration de l'original).
     */
    const saveKey = React.useCallback(async (i18nKey, byLng) => {
        const next = readStore(config);
        LABEL_LNGS.forEach((l) => {
            const v = (byLng[l] ?? "").trim();
            if (v) next.keys[l][i18nKey] = v;
            else delete next.keys[l][i18nKey];
            applyKeyToI18n(i18nKey, v, l);
        });
        await persist(next);
    }, [config, persist, applyKeyToI18n]);

    /**
     * Enregistre en un seul appel le libellé d'un champ (clé i18n), les libellés
     * de ses valeurs de données, ET la visibilité (champ / valeurs).
     *  - fieldI18nKey / fieldByLng : le libellé du champ lui-même
     *  - field                     : identifiant du champ pour les valeurs
     *  - valueRows                 : [{ value, fr, en }]
     *  - visibility                : { fieldHidden: bool, hiddenValues: string[] }
     */
    const saveField = React.useCallback(async (fieldI18nKey, fieldByLng, field, valueRows, visibility) => {
        const next = readStore(config);
        if (fieldI18nKey) {
            LABEL_LNGS.forEach((l) => {
                const v = (fieldByLng?.[l] ?? "").trim();
                if (v) next.keys[l][fieldI18nKey] = v;
                else delete next.keys[l][fieldI18nKey];
                applyKeyToI18n(fieldI18nKey, v, l);
            });
        }
        (valueRows || []).forEach(({value, fr, en}) => {
            const byLng = {fr, en};
            LABEL_LNGS.forEach((l) => {
                const v = (byLng[l] ?? "").trim();
                if (!next.values[l][field]) next.values[l][field] = {};
                if (v) next.values[l][field][value] = v;
                else delete next.values[l][field][value];
                if (next.values[l][field] && Object.keys(next.values[l][field]).length === 0) {
                    delete next.values[l][field];
                }
            });
        });
        if (visibility) {
            const fields = new Set(next.hidden.fields);
            if (visibility.fieldHidden) fields.add(field);
            else fields.delete(field);
            next.hidden.fields = [...fields];

            const hv = visibility.hiddenValues || [];
            if (hv.length > 0) next.hidden.values[field] = [...hv];
            else delete next.hidden.values[field];
        }
        await persist(next);
    }, [config, persist, applyKeyToI18n]);

    const value = React.useMemo(() => ({
        editMode,
        canEdit,
        lng,
        lngs: LABEL_LNGS,
        lngMeta: LABEL_LNG_META,
        getKeyLabel,
        getKeyOverride,
        resolveValue,
        resolveValueOr,
        getValueOverride,
        isFieldHidden,
        isValueHidden,
        getHiddenValues,
        visibleValues,
        saveKey,
        saveField,
    }), [editMode, canEdit, lng, getKeyLabel, getKeyOverride, resolveValue, resolveValueOr, getValueOverride,
        isFieldHidden, isValueHidden, getHiddenValues, visibleValues, saveKey, saveField]);

    return (
        <LabelOverridesContext.Provider value={value}>
            {children}
        </LabelOverridesContext.Provider>
    );
}

/**
 * Hook de consommation. Renvoie un objet inerte (édition désactivée, résolution
 * = identité/`t`) si aucun provider n'est monté, pour que les composants restent
 * utilisables hors du contexte réanimation.
 */
export function useLabelOverrides() {
    const ctx = React.useContext(LabelOverridesContext);
    if (ctx) return ctx;
    return FALLBACK_CTX;
}

const FALLBACK_CTX = {
    editMode: false,
    canEdit: false,
    lng: "fr",
    lngs: LABEL_LNGS,
    lngMeta: LABEL_LNG_META,
    getKeyLabel: (_key, fallback) => fallback,
    getKeyOverride: () => ({fr: "", en: ""}),
    resolveValue: (_field, rawValue) => rawValue,
    resolveValueOr: (_field, _rawValue, fallback) => fallback,
    getValueOverride: () => ({fr: "", en: ""}),
    isFieldHidden: () => false,
    isValueHidden: () => false,
    getHiddenValues: () => [],
    visibleValues: (_field, list) => list || [],
    saveKey: async () => {},
    saveField: async () => {},
};

export default LabelOverridesContext;
